import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/components/providers/auth-provider'
import { useXAuth } from '@/hooks/use-x-auth'
import { requestSignIn } from '@/lib/guest'

const REASONS: Record<string, string> = {
  limit: 'You used the demo messages. Sign in to keep going with your own voice.',
  post: 'Posting goes out from your own X account.',
}

function XMark() {
  return <span aria-hidden className="text-base font-semibold leading-none">𝕏</span>
}

/** The one place the demo turns into a real account. */
export function SignInWithXButton({ className, label = 'Sign in with X' }: { className?: string; label?: string }) {
  const { initiateXAuth, loading } = useXAuth()
  return (
    <Button onClick={() => initiateXAuth()} disabled={loading} className={className}>
      <XMark />
      {loading ? 'Opening X' : label}
    </Button>
  )
}

/** Listens for sign-in requests from anywhere (Apollo, chat cards) and asks once. */
export function GuestSignInDialog() {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<string | undefined>()

  useEffect(() => {
    const onRequest = (event: Event) => {
      setReason((event as CustomEvent).detail?.reason)
      setOpen(true)
    }
    window.addEventListener('x11:sign-in-required', onRequest)
    return () => window.removeEventListener('x11:sign-in-required', onRequest)
  }, [])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Sign in with X to do that</DialogTitle>
          <DialogDescription>
            {(reason && REASONS[reason]) ||
              'The demo can chat, draft and score. Posting, scheduling and calls go through your own X account.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 pt-2">
          <SignInWithXButton className="w-full" />
          <Button variant="ghost" onClick={() => setOpen(false)}>Keep exploring</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** A thin bar over every page while a guest is looking around. */
export function GuestBar() {
  const { user } = useAuth() as any
  if (!user?.isGuest) return null
  return (
    <div className="border-b border-border bg-muted/40">
      <div className="flex items-center justify-between gap-3 px-4 py-1.5 text-xs md:px-6">
        <p className="min-w-0 truncate text-muted-foreground">
          <span className="font-medium text-foreground">Demo.</span>{' '}
          You are writing as Ada Merrow, a made-up creator. One idea gets you scored takes; voice is off, and posting needs your X account.
        </p>
        <div className="flex shrink-0 items-center gap-3">
          <Link to="/" className="hidden text-muted-foreground hover:text-foreground sm:inline">Home</Link>
          <Button size="sm" className="h-7 px-2.5 text-xs" onClick={() => requestSignIn()}>
            <XMark />
            Sign in
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Inline card the chat renders when a guest reaches something only an account can do. */
export function SignInRequiredCard({ reason }: { reason?: string }) {
  return (
    <div className="surface-slab flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
      <div className="min-w-0">
        <p className="text-sm font-medium">{reason === 'limit' ? 'Demo messages used up' : 'This needs your X account'}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {reason === 'limit'
            ? 'Sign in to keep going, with your real voice learned from your posts.'
            : 'Sign in with X and it posts from your account. Your drafts here stay.'}
        </p>
      </div>
      <SignInWithXButton className="shrink-0" />
    </div>
  )
}

/**
 * Takes the composer's place once a guest has their first options card.
 * The demo is one composition; the server refuses the next one anyway.
 */
export function GuestDemoDone() {
  return (
    <div className="float-slab rounded-xl border border-border p-4">
      <p className="text-sm font-medium text-foreground">That's the demo.</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Those takes were written as Ada, who is made up. Sign in with X and the next ones are written as you, from
        your own posts, and you can keep chatting, schedule and post.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <SignInWithXButton label="Sign in with X to keep going" />
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Back to home</Link>
      </div>
    </div>
  )
}
